import {
  CHECK_IN_CLOSES_AFTER_END_OPTIONS,
  CHECK_IN_OPENS_BEFORE_START_OPTIONS,
  CHECK_IN_WINDOW_SIDES,
  DEFAULT_CHECK_IN_WINDOW,
} from '../constants/schedule.constants';
import type { LANG } from '../constants/languages';

export type EducationalEventsLang = LANG['EDUCATIONAL_EVENTS'];

export type CheckInWindowSide = (typeof CHECK_IN_WINDOW_SIDES)[keyof typeof CHECK_IN_WINDOW_SIDES];

/** Check-in offsets in minutes: before the event's start, after its end. */
export interface CheckInWindow {
  checkInOpensBeforeStartMinutes: number;
  checkInClosesAfterEndMinutes: number;
}

/** Form-side offsets: select string tokens or numbers. */
export type CheckInWindowFormValues = {
  [K in keyof CheckInWindow]?: string | number;
};

export interface CheckInWindowEvent {
  attributes?: {
    checkInOpensBeforeStartMinutes?: unknown;
    checkInClosesAfterEndMinutes?: unknown;
  } | null;
}

/** A date-library instant (moment, dayjs) as the forms hold it. */
interface DateLibraryInstant {
  toDate: () => Date;
}

export type CheckInWindowEventTime = string | number | Date | DateLibraryInstant | null;

/** The event times the edit form's check-in window rules read. */
export interface CheckInWindowEventTimes {
  startTime?: CheckInWindowEventTime;
  endTime?: CheckInWindowEventTime;
}

export interface CheckInOffsetOption {
  value: string;
  label: string;
}

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
const MS_PER_MINUTE = 60 * 1000;

const PRESET_OFFSETS_BY_SIDE: Record<CheckInWindowSide, number[]> = {
  [CHECK_IN_WINDOW_SIDES.OPEN]: CHECK_IN_OPENS_BEFORE_START_OPTIONS,
  [CHECK_IN_WINDOW_SIDES.CLOSE]: CHECK_IN_CLOSES_AFTER_END_OPTIONS,
};

const toTimestamp = (value: CheckInWindowEventTime | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const date = typeof value === 'object' && 'toDate' in value ? value.toDate() : new Date(value);
  const timestamp = date.getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

const storedOffsetOr = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isInteger(value) ? value : fallback;

/**
 * The event's check-in offsets. An event that stores none is read as the
 * defaults (0/0, "At start" / "At end"), as the backend does.
 */
export function getCheckInWindowFromEvent(
  event: CheckInWindowEvent | null | undefined,
): CheckInWindow {
  const { checkInOpensBeforeStartMinutes, checkInClosesAfterEndMinutes } = event?.attributes ?? {};
  return {
    checkInOpensBeforeStartMinutes: storedOffsetOr(
      checkInOpensBeforeStartMinutes,
      DEFAULT_CHECK_IN_WINDOW.checkInOpensBeforeStartMinutes,
    ),
    checkInClosesAfterEndMinutes: storedOffsetOr(
      checkInClosesAfterEndMinutes,
      DEFAULT_CHECK_IN_WINDOW.checkInClosesAfterEndMinutes,
    ),
  };
}

/**
 * Normalizes form values to the offsets the API takes; a missing value falls
 * back to its default.
 */
export function toCheckInWindowMinutes({
  checkInOpensBeforeStartMinutes = DEFAULT_CHECK_IN_WINDOW.checkInOpensBeforeStartMinutes,
  checkInClosesAfterEndMinutes = DEFAULT_CHECK_IN_WINDOW.checkInClosesAfterEndMinutes,
}: CheckInWindowFormValues = {}): CheckInWindow {
  return {
    checkInOpensBeforeStartMinutes: Number(checkInOpensBeforeStartMinutes),
    checkInClosesAfterEndMinutes: Number(checkInClosesAfterEndMinutes),
  };
}

/**
 * The offset attributes to PATCH: both offsets when either changed (the API
 * rejects one without the other), otherwise nothing.
 */
export function buildCheckInWindowPatch({
  event,
  values,
}: {
  event: CheckInWindowEvent | null | undefined;
  values: CheckInWindowFormValues;
}): Partial<CheckInWindow> {
  const current = getCheckInWindowFromEvent(event);
  const next = toCheckInWindowMinutes(values);
  const changed =
    next.checkInOpensBeforeStartMinutes !== current.checkInOpensBeforeStartMinutes ||
    next.checkInClosesAfterEndMinutes !== current.checkInClosesAfterEndMinutes;
  return changed ? next : {};
}

/** Duration in words: "30 min", "1 hour", "2 hours", "1 day", "1 day 2 hours". */
export function formatCheckInDuration({
  minutes,
  lang,
}: {
  minutes: number;
  lang: EducationalEventsLang;
}): string {
  const days = Math.floor(minutes / MINUTES_PER_DAY);
  const hours = Math.floor((minutes % MINUTES_PER_DAY) / MINUTES_PER_HOUR);
  const remainingMinutes = minutes % MINUTES_PER_HOUR;
  const dayTemplate = days === 1 ? lang.CHECK_IN_DURATION_DAYS_ONE : lang.CHECK_IN_DURATION_DAYS;
  const hourTemplate =
    hours === 1 ? lang.CHECK_IN_DURATION_HOURS_ONE : lang.CHECK_IN_DURATION_HOURS;
  const parts: [number, string][] = [
    [days, dayTemplate],
    [hours, hourTemplate],
    [remainingMinutes, lang.CHECK_IN_DURATION_MINUTES],
  ];
  return parts
    .filter(([count]) => count > 0)
    .map(([count, template]) => template.replace('{count}', String(count)))
    .join(' ');
}

/**
 * Label for one side of the window: "At start" / "30 min before" for
 * `CHECK_IN_WINDOW_SIDES.OPEN`, "At end" / "1 day after" for `CLOSE`.
 */
export function formatOffsetLabel({
  minutes,
  lang,
  side,
}: {
  minutes: number;
  lang: EducationalEventsLang;
  side: CheckInWindowSide;
}): string {
  const isOpenSide = side === CHECK_IN_WINDOW_SIDES.OPEN;
  if (!minutes) return isOpenSide ? lang.CHECK_IN_OFFSET_AT_START : lang.CHECK_IN_OFFSET_AT_END;
  const template = isOpenSide ? lang.CHECK_IN_OFFSET_BEFORE_START : lang.CHECK_IN_OFFSET_AFTER_END;
  return template.replace('{duration}', formatCheckInDuration({ minutes, lang }));
}

/** Whether the event's start time has been reached. An event without a start time hasn't started. */
export function hasEventStarted({
  event,
  now = Date.now(),
}: {
  event: CheckInWindowEventTimes | null | undefined;
  now?: number;
}): boolean {
  const start = toTimestamp(event?.startTime);
  return start !== null && now >= start;
}

/**
 * Select options for one side of the window, values as string tokens. A stored
 * offset outside the preset list (set through the API) is kept as an option so
 * the select can still show it.
 *
 * When `event` is passed (edit mode), the close side only offers offsets whose
 * `event.endTime + minutes` is still after `now`; the stored offset is always
 * kept. Pass the end time currently in the form (falling back to the stored
 * one), so moving the end later brings options back. Before the event starts
 * every preset passes, so nothing changes.
 */
export function getCheckInOffsetOptions({
  lang,
  side,
  currentMinutes,
  event,
  now = Date.now(),
}: {
  lang: EducationalEventsLang;
  side: CheckInWindowSide;
  currentMinutes?: number;
  event?: CheckInWindowEventTimes | null;
  now?: number;
}): CheckInOffsetOption[] {
  const end = side === CHECK_IN_WINDOW_SIDES.CLOSE ? toTimestamp(event?.endTime) : null;
  const presets = PRESET_OFFSETS_BY_SIDE[side].filter(
    minutes => end === null || end + minutes * MS_PER_MINUTE > now,
  );
  const minutesList =
    currentMinutes === undefined || presets.includes(currentMinutes)
      ? presets
      : [...presets, currentMinutes].sort((a, b) => a - b);
  return minutesList.map(minutes => ({
    value: String(minutes),
    label: formatOffsetLabel({ minutes, lang, side }),
  }));
}

/**
 * "30 min before - 1 hour after"; "During the event" for the default window
 * (0/0), which is also how an event without offsets reads.
 */
export function formatCheckInWindowLabel({
  event,
  lang,
}: {
  event: CheckInWindowEvent | null | undefined;
  lang: EducationalEventsLang;
}): string {
  const { checkInOpensBeforeStartMinutes, checkInClosesAfterEndMinutes } =
    getCheckInWindowFromEvent(event);
  if (!checkInOpensBeforeStartMinutes && !checkInClosesAfterEndMinutes) {
    return lang.CHECK_IN_WINDOW_DURING_EVENT;
  }
  return lang.CHECK_IN_WINDOW_VALUE.replace(
    '{opens}',
    formatOffsetLabel({
      minutes: checkInOpensBeforeStartMinutes,
      lang,
      side: CHECK_IN_WINDOW_SIDES.OPEN,
    }),
  ).replace(
    '{closes}',
    formatOffsetLabel({
      minutes: checkInClosesAfterEndMinutes,
      lang,
      side: CHECK_IN_WINDOW_SIDES.CLOSE,
    }),
  );
}

export interface CheckInTimingEvent extends CheckInWindowEventTimes {
  checkIn?: { isOpen?: boolean; opensAt?: string | null; closesAt?: string | null } | null;
  attributes?: {
    checkInOpensBeforeStartMinutes?: unknown;
    checkInClosesAfterEndMinutes?: unknown;
    checkInSession?: { openedAt?: string | null; closedAt?: string | null } | null;
  } | null;
}

/**
 * When check-in next opens, if that is still ahead. The backend's `checkIn.opensAt`
 * wins; an event without `checkIn` (an older backend) falls back to its start minus
 * the window's open offset.
 *
 * @param params.event - The event.
 * @param params.now - The current time in ms. Default `Date.now()`.
 * @returns The opening time in ms, or null when check-in is open, over or not scheduled.
 * @example
 * const opensAt = getNextCheckInOpen({ event });
 */
export function getNextCheckInOpen({
  event,
  now = Date.now(),
}: {
  event: CheckInTimingEvent | null | undefined;
  now?: number;
}): number | null {
  if (event?.checkIn) {
    if (event.checkIn.isOpen) return null;
    const opensAt = toTimestamp(event.checkIn.opensAt ?? null);
    return opensAt !== null && opensAt > now ? opensAt : null;
  }
  const start = toTimestamp(event?.startTime);
  if (start === null) return null;
  const opensAt =
    start - getCheckInWindowFromEvent(event).checkInOpensBeforeStartMinutes * MS_PER_MINUTE;
  return opensAt > now ? opensAt : null;
}

/**
 * When check-in closed: the host's manual close, or else the end of the window once
 * it has passed. Null while check-in is open or still ahead.
 *
 * @param params.event - The event.
 * @param params.now - The current time in ms. Default `Date.now()`.
 * @returns The closing time in ms, or null.
 * @example
 * const closedAt = getCheckInClosedAt({ event });
 */
export function getCheckInClosedAt({
  event,
  now = Date.now(),
}: {
  event: CheckInTimingEvent | null | undefined;
  now?: number;
}): number | null {
  if (event?.checkIn?.isOpen) return null;
  if (getNextCheckInOpen({ event, now }) !== null) return null;
  const closedAt = toTimestamp(event?.attributes?.checkInSession?.closedAt ?? null);
  if (closedAt !== null && closedAt <= now) return closedAt;
  const end = toTimestamp(event?.endTime);
  if (end === null) return null;
  const windowEnd =
    end + getCheckInWindowFromEvent(event).checkInClosesAfterEndMinutes * MS_PER_MINUTE;
  return windowEnd <= now ? windowEnd : null;
}
